import { useState, useEffect, useMemo, useCallback } from 'react';
import type { Customer, CustomerKind } from '../services/customerService';

const STORAGE_KEY = 'twm_customers_filter_state';

export interface CustomerFilterState {
  customerKinds: CustomerKind[];
  tags: string[];
  /**
   * Filter sidebar open/closed. Owned here (not by useLayout) because the
   * LeftRail forces useLayout's sidebarOpen back to true on every navigation,
   * which would clobber a user's choice to keep the panel closed.
   */
  sidebarCollapsed: boolean;
}

const EMPTY_STATE: CustomerFilterState = {
  customerKinds: [],
  tags: [],
  sidebarCollapsed: false,
};

function loadState(): CustomerFilterState {
  if (typeof window === 'undefined') return EMPTY_STATE;
  const isMobile = window.matchMedia('(max-width: 768px)').matches;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    // Defensive merge — if the stored shape is missing keys (older version
    // of the app), fill them with empty defaults so destructuring is safe.
    const base = raw ? { ...EMPTY_STATE, ...JSON.parse(raw) } : EMPTY_STATE;
    return isMobile ? { ...base, sidebarCollapsed: true } : base;
  } catch {
    return isMobile ? { ...EMPTY_STATE, sidebarCollapsed: true } : EMPTY_STATE;
  }
}

function saveState(state: CustomerFilterState) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Quota / private mode — silently fail; filters reset to defaults next
    // visit, no other behavior breaks.
  }
}

export interface UseCustomerFiltersResult {
  /** Current filter state. */
  state: CustomerFilterState;
  /** Update a single filter field; persists to localStorage. */
  setField: <K extends keyof CustomerFilterState>(key: K, value: CustomerFilterState[K]) => void;
  /** Reset all filters (preserves sidebar collapsed state). */
  clearAll: () => void;
  /** True if any filter is set (excludes sidebar collapse state). */
  hasActiveFilters: boolean;
  /** Count of active filter dimensions (kinds, tags, regions each count as 1 if any). */
  activeFilterCount: number;
  /**
   * Apply the current filter state to a customer list. Pure derivation —
   * call from a useMemo that depends on `state` and the input list.
   */
  applyFilters: (customers: Customer[]) => Customer[];
}

export function useCustomerFilters(): UseCustomerFiltersResult {
  const [state, setState] = useState<CustomerFilterState>(loadState);

  useEffect(() => {
    saveState(state);
  }, [state]);

  const setField = useCallback(
    <K extends keyof CustomerFilterState>(key: K, value: CustomerFilterState[K]) => {
      setState((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const clearAll = useCallback(() => {
    // Preserve sidebar collapsed state — the user is likely about to refilter,
    // so closing the panel as a side effect would be surprising.
    setState((prev) => ({ ...EMPTY_STATE, sidebarCollapsed: prev.sidebarCollapsed }));
  }, []);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (state.customerKinds.length > 0) n++;
    if (state.tags.length > 0) n++;
    return n;
  }, [state]);

  const hasActiveFilters = activeFilterCount > 0;

  const applyFilters = useCallback<UseCustomerFiltersResult['applyFilters']>(
    (customers) => {
      return customers.filter((c) => {
        // Customer kind: empty array = no filter; non-empty = include only
        // records whose kind is in the selection.
        if (state.customerKinds.length > 0) {
          const kind = c.customerKind || 'individual';
          if (!state.customerKinds.includes(kind)) return false;
        }

        // Tags: include rows that have ANY of the selected tags (OR within
        // dimension). Cross-dimension is AND.
        if (state.tags.length > 0) {
          const rowTags = c.tags || [];
          if (!state.tags.some((t) => rowTags.includes(t))) return false;
        }

        return true;
      });
    },
    [state],
  );

  return { state, setField, clearAll, hasActiveFilters, activeFilterCount, applyFilters };
}
